import React, { useState, useCallback, useRef } from "react";
import {
  Stack,
  Typography,
  Divider,
  IconButton,
  Box,
  Skeleton,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@material-ui/icons/Close";
import EmailIcon from "@mui/icons-material/Email";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { colors } from "../../../utils";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { ASSETS } from "../../../utils/config";
import InfiniteScroll from "react-infinite-scroll-component";
import LockSettingModalHeader from "./LockSettingModalHeader";
import Input from "../../Input";
import { useDeleteWheelEmailPermission } from "../../../services/mutations";
import { toast } from "react-toastify";
import { getDoNotShowDeletePopupAgainValue } from "../../../utils/localStorageHelper";

const EmailListingSkeleton = () => {
  return (
    <div style={{ minHeight: "40vh" }}>
      {[...Array(5)].map((_, index) => (
        <>
          <Stack mx={1} my={2} gap={2} key={index}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Skeleton variant="rectangular" width={24} height={24} />
              <Skeleton variant="text" width={250} height={20} />
            </Stack>
          </Stack>
          <Divider sx={{ borderWidth: 1, my: 0.8 }} variant="fullWidth" />
        </>
      ))}
    </div>
  );
};

const NoEmailsAvailable = ({ onGoBackPress }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "50vh",
        textAlign: "center",
        px: 3,
      }}
    >
      <Card
        elevation={0}
        sx={{
          maxWidth: 400,
          width: "100%",
          backgroundColor: "rgba(239, 233, 254, 0.3)",
          border: "1px solid rgba(239, 233, 254, 0.5)",
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ py: 4 }}>
          <EmailIcon
            sx={{
              fontSize: 64,
              color: colors.PrimaryPurple300,
              mb: 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontFamily: "DM Sans",
              fontWeight: 600,
              color: "#565556",
              mb: 1,
            }}
          >
            No Emails Added Yet
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "DM Sans",
              color: "#888",
              mb: 3,
              lineHeight: 1.5,
            }}
          >
            You haven't added any email addresses yet. Add emails to grant
            access to your wheel.
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={onGoBackPress}
            sx={{
              backgroundColor: colors.PrimaryPurple,
              color: "white",
              textTransform: "none",
              fontFamily: "DM Sans",
              fontWeight: 500,
              borderRadius: 2,
              px: 3,
              py: 1.5,
              "&:hover": {
                backgroundColor: colors.PrimaryPurple500,
              },
            }}
          >
            Add Emails
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

const EmailListing = ({
  wheelId,
  onGoBackPress = () => {},
  data,
  fetchNextPage,
  hasNextPage,
  isFetching,
  isError,
  error,
  onSearch,
}) => {
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [singleEmailToDelete, setSingleEmailToDelete] = useState(null); // For individual delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const debounceTimeoutRef = useRef(null);

  // Debounced search function
  const debouncedSearch = useCallback(
    (value) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        onSearch(value);
      }, 500); // 500ms delay
    },
    [onSearch]
  );

  const { mutate: deleteEmailPermission, isLoading: isDeleting } =
    useDeleteWheelEmailPermission();

  const emails = data?.pages.flatMap((page) => page.results) || [];
  const isDoNotShowAgainChecked = getDoNotShowDeletePopupAgainValue();

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const triggerDeleteModal = (email = null) => {
    const emailsToDelete = email ? [email] : [...selectedEmails];

    if (!isDoNotShowAgainChecked) {
      setSingleEmailToDelete(email);
      setIsDeleteModalOpen(true);
    } else {
      handleDelete(emailsToDelete); // Pass latest state explicitly
    }
  };

  const handleDelete = (emailsToDelete) => {
    if (!emailsToDelete || emailsToDelete.length === 0) return;

    deleteEmailPermission(
      { wheelId, emails: emailsToDelete },
      {
        onSuccess: () => {
          toast.success("Emails removed successfully!");
          setSelectedEmails([]);
          setSingleEmailToDelete(null);
          setIsDeleteModalOpen(false);
        },
        onError: (error) => {
          toast.error("Error removing email:", error);
        },
      }
    );
  };

  // const handleDelete = () => {
  //   const emailsToDelete = singleEmailToDelete
  //     ? [singleEmailToDelete]
  //     : selectedEmails;

  //   console.log("emailsToDelete", emailsToDelete);

  //   deleteEmailPermission(
  //     { wheelId, emails: emailsToDelete },
  //     {
  //       onSuccess: () => {
  //         toast.success("Emails removed successfully!");
  //         setSelectedEmails([]);
  //         setSingleEmailToDelete(null);
  //         setIsDeleteModalOpen(false);
  //       },
  //       onError: (error) => {
  //         toast.error("Error removing email:", error);
  //       },
  //     }
  //   );
  // };

  if (isError) return <p>Error: {error.message}</p>;

  return (
    <div>
      <LockSettingModalHeader
        onBackIconClick={onGoBackPress}
        showBackButton={true}
      ></LockSettingModalHeader>
      <Divider sx={{ my: 2, borderWidth: 1 }} variant="fullWidth" />
      <Stack
        gap={2}
        sx={{ my: 2 }}
        direction="row"
        alignItems="center"
        justifyContent={"space-between"}
      >
        <Stack
          gap={2}
          direction={"row"}
          alignItems={"center"}
          sx={{ width: "20vw" }}
        >
          <div
            style={{
              padding: 10,
              backgroundColor: "rgba(239, 233, 254, 1)",
              borderRadius: 5,
            }}
          >
            <img
              alt="rightIcon"
              src={ASSETS.segmentEditor.LockWheelEmailListIcon}
              style={{ height: "24px", width: "24px" }}
            />
          </div>
          <Typography
            style={{
              fontFamily: "DM Sans",
              fontSize: 16,
              fontWeight: 500,
              color: "#000000",
            }}
          >
            Emails with Access
          </Typography>
        </Stack>

        <Input
          onChange={(e) => debouncedSearch(e.target.value)}
          isSearch={true}
          containerStyle={{ marginBottom: 0 }}
        ></Input>
      </Stack>

      <div
        id="EmailListscrollableDiv"
        style={{ height: "50vh", overflowY: "auto" }}
      >
        {isFetching ? (
          <EmailListingSkeleton />
        ) : emails?.length === 0 ? (
          <NoEmailsAvailable onGoBackPress={onGoBackPress} />
        ) : (
          <InfiniteScroll
            dataLength={emails.length}
            next={() => {
              console.log("fetchNextPage");
              fetchNextPage();
            }}
            hasMore={hasNextPage}
            // loader={<p>Loading more emails...</p>}
            scrollableTarget="EmailListscrollableDiv"
            scrollThreshold={0.8}
            // endMessage={<p>No more emails.</p>}
          >
            {emails?.length > 0 &&
              emails?.map((email, index) => (
                <div key={email}>
                  <Stack direction="row" alignItems="center" gap={2}>
                    <div>
                      <Checkbox
                        checked={selectedEmails.includes(email)}
                        onChange={(e) => {
                          setSelectedEmails(
                            e.target.checked
                              ? [...selectedEmails, email]
                              : selectedEmails.filter((e) => e !== email)
                          );
                        }}
                        sx={{
                          color: colors.PrimaryPurple300,
                          "&.Mui-checked": {
                            color: colors.PrimaryPurple500,
                          },
                        }}
                      />
                    </div>
                    <Typography
                      style={{
                        fontFamily: "DM Sans",
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#565556",
                      }}
                    >
                      {email}
                    </Typography>
                    <IconButton onClick={() => triggerDeleteModal(email)}>
                      <CloseIcon style={{ color: "#BBBBBB", fontSize: 14 }} />
                    </IconButton>
                  </Stack>
                  {emails.length !== index + 1 && (
                    <Divider
                      sx={{ borderWidth: 1, my: 0.8 }}
                      variant="fullWidth"
                    />
                  )}
                </div>
              ))}
          </InfiniteScroll>
        )}
      </div>

      <Divider sx={{ my: 2, borderWidth: 1 }} variant="fullWidth" />

      <Box style={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        {emails.length > 0 && selectedEmails.length > 0 && !isFetching && (
          <Button
            loading={isDeleting}
            onClick={() => triggerDeleteModal()}
            loadingPosition="start"
            startIcon={<CloseIcon style={{ color: colors.error }} />}
            variant="outlined"
            color="error"
          >
            Delete
          </Button>
        )}
      </Box>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() =>
          handleDelete(
            singleEmailToDelete ? [singleEmailToDelete] : selectedEmails
          )
        }
      />
    </div>
  );
};

export default EmailListing;
